import type { EndpointMap, ListResponse } from '../common';
import type {
  ExportInvestmentSetsRequest,
  ListInvestmentSetsRequest,
  PortfolioStatisticsRequest,
} from './indicator.request';
import type {
  FundReportCutoff,
  InvestmentSetListItem,
  InvestmentTimelineItem,
  PortfolioCaptable,
  PortfolioEvaluationItem,
  PortfolioInvestmentSets,
  PortfolioManagementItem,
  PortfolioStatisticsPeriod,
  RoundShareItem,
  TotalSummary,
} from './indicator.response';

/**
 * 지표 전용 엔드포인트 — 자원 하나에 딸리지 않거나(전체 · 펀드×포트폴리오) 여러 소스가 섞인(투자 이력 · 캡테이블) 지표.
 * 자원 자체의 지표는 그 자원 응답의 `indicators` 에 있다(펀드 목록 · 상세, 포트폴리오 상세, D3 투자 상세).
 * 조회 범위는 그 자원과 같다(LP = 출자 펀드 · 공개 분기까지, 포트폴리오 = 자기 회사).
 * 경로 id 가 정수가 아니면 BAD_REQUEST_INVALID_PARAMETERS, 범위 밖 포트폴리오 · 투자는 없는 것과 같다(NOT_FOUND_*).
 */
export const indicatorEndpoints = {
  /** 전체 요약(D3 · LP) */
  getTotalSummary: {
    method: 'GET',
    path: '/indicators/summary',
    res: {} as TotalSummary,
  },
  /** 펀드별 LP 공개 분기(D3) */
  listFundReportCutoffs: {
    method: 'GET',
    path: '/indicators/fund-cutoffs',
    res: {} as ListResponse<FundReportCutoff>,
  },
  listInvestmentSets: {
    method: 'GET',
    path: '/investment-sets',
    req: {} as ListInvestmentSetsRequest,
    res: {} as ListResponse<InvestmentSetListItem>,
    /** FORBIDDEN: D3 가 아닌 역할이 mine=true */
    errors: ['BAD_REQUEST_INVALID_PARAMETERS', 'FORBIDDEN'],
  },
  /**
   * 엑셀 내보내기(D3) — 응답은 xlsx 파일(Content-Disposition: attachment). 펀드 · 투자일 순.
   * BAD_REQUEST_INVALID_PARAMETERS: type · `fundIds[i]` 형식(invalid_type) · 없는 펀드(not_found)
   */
  exportInvestmentSets: {
    method: 'GET',
    path: '/investment-sets/export',
    req: {} as ExportInvestmentSetsRequest,
    res: {} as ArrayBuffer,
    errors: ['BAD_REQUEST_INVALID_PARAMETERS'],
  },
  /** 포트폴리오의 펀드별 투자 지표(D3 · LP) */
  getPortfolioInvestmentSets: {
    method: 'GET',
    path: '/portfolios/:portfolioId/investment-sets',
    res: {} as PortfolioInvestmentSets,
    errors: ['BAD_REQUEST_INVALID_PARAMETERS', 'NOT_FOUND_PORTFOLIO'],
  },
  /** 라운드별 총 주식 수 · D3 주식 수(모든 역할) */
  listPortfolioRoundShares: {
    method: 'GET',
    path: '/portfolios/:portfolioId/round-shares',
    res: {} as ListResponse<RoundShareItem>,
    errors: ['BAD_REQUEST_INVALID_PARAMETERS', 'NOT_FOUND_PORTFOLIO'],
  },
  /** 캡테이블(D3) — 라운드(같은 이름 합침) + 현재 */
  getPortfolioCaptable: {
    method: 'GET',
    path: '/portfolios/:portfolioId/captable',
    res: {} as PortfolioCaptable,
    errors: ['BAD_REQUEST_INVALID_PARAMETERS', 'NOT_FOUND_PORTFOLIO'],
  },
  /** D3 투자의 이벤트별 누적 상태 */
  getInvestmentTimeline: {
    method: 'GET',
    path: '/investments/:id/timeline',
    res: {} as ListResponse<InvestmentTimelineItem>,
    errors: ['BAD_REQUEST_INVALID_PARAMETERS', 'NOT_FOUND_INVESTMENT'],
  },
  /** 포트폴리오 평가 통계(D3) — 기간별 */
  listPortfolioEvaluation: {
    method: 'GET',
    path: '/indicators/portfolio-evaluation',
    req: {} as PortfolioStatisticsRequest,
    res: {} as ListResponse<PortfolioStatisticsPeriod<PortfolioEvaluationItem>>,
    errors: ['BAD_REQUEST_INVALID_PARAMETERS'],
  },
  /** 포트폴리오 관리 통계(D3) — 기간별 D3 평가 항목 */
  listPortfolioManagement: {
    method: 'GET',
    path: '/indicators/portfolio-management',
    req: {} as PortfolioStatisticsRequest,
    res: {} as ListResponse<PortfolioStatisticsPeriod<PortfolioManagementItem>>,
    errors: ['BAD_REQUEST_INVALID_PARAMETERS'],
  },
} as const satisfies EndpointMap;
